import { useCallback, useEffect, useState } from 'react'
import { Pagination, Spin, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { getPortfolioRebalances } from '@/api/portfolio'
import { getTradingOrders } from '@/api/paperTrading'
import type { RebalanceItem } from '@/types/portfolio'
import type { TradingOrder } from '@/types/paperTrading'
import { filterFormatThousandNumber, filterFormatWanYiNumber } from '@/utils/filters'

const PAGE_SIZE = 10

const toneConfig: Record<string, { label: string; color: string }> = {
    buy: { label: '买入', color: '#4f8cff' },
    add: { label: '增持', color: '#22c55e' },
    reduce: { label: '减持', color: '#f97316' },
    sell: { label: '卖出', color: '#ef4444' },
}

const statusLabel: Record<string, string> = {
    accepted: '已受理',
    submitted: '已提交',
    partially_filled: '部分成交',
    filled: '已成交',
    cancelled: '已撤单',
    canceled: '已撤单',
    rejected: '已拒绝',
    executed: '已执行',
    pending: '处理中',
}

function resolveTone(item: RebalanceItem): 'buy' | 'add' | 'reduce' | 'sell' {
    if (item.tone === 'buy' || item.tone === 'add' || item.tone === 'reduce') {
        if (item.tone === 'reduce' && /卖|sell/i.test(item.action || '')) {
            return 'sell'
        }
        return item.tone
    }
    if (item.tone === 'sell') return 'sell'
    return 'reduce'
}

function fmtTradeAmount(v: number) {
    const abs = Math.abs(v)
    if (abs <= 0) return '--'
    if (abs >= 10000) return filterFormatWanYiNumber(abs)
    return filterFormatThousandNumber(abs, 2)
}

function fmtShares(v: number) {
    if (!Number.isFinite(v) || v <= 0) return '--'
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function fmtPrice(v: number) {
    if (!Number.isFinite(v) || v <= 0) return '--'
    return v.toFixed(v >= 100 ? 2 : 3)
}

function num(v: string | number | null | undefined) {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
}

function orderStockName(order: TradingOrder) {
    return (order.name || order.stock_name || '').trim() || order.symbol || '--'
}

const fillColumns: ColumnsType<TradingOrder> = [
    {
        title: '成交时间',
        dataIndex: 'created_at',
        width: 170,
        render: (v: string) => (v ? new Date(v).toLocaleString() : '--'),
    },
    {
        title: '方向',
        dataIndex: 'side',
        width: 80,
        render: (side: string) => (
            <Tag color={side === 'buy' ? 'green' : 'red'}>
                {side === 'buy' ? '买入' : '卖出'}
            </Tag>
        ),
    },
    {
        title: '标的',
        key: 'symbol',
        render: (_: unknown, row) => {
            const name = orderStockName(row)
            return (
                <div>
                    <div style={{ color: '#fff', fontWeight: 600 }}>{name}</div>
                    {row.symbol && name !== row.symbol && (
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{row.symbol}</div>
                    )}
                </div>
            )
        },
    },
    {
        title: '成交数量',
        align: 'right',
        render: (_: unknown, row) => fmtShares(num(row.filled_qty)),
    },
    {
        title: '成交均价',
        align: 'right',
        render: (_: unknown, row) => fmtPrice(num(row.avg_fill_price)),
    },
    {
        title: '成交金额',
        align: 'right',
        render: (_: unknown, row) => {
            const amount = num(row.filled_qty) * num(row.avg_fill_price)
            const isBuy = row.side === 'buy'
            if (amount <= 0) return '--'
            return (
                <span style={{ color: isBuy ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                    {isBuy ? '+' : '-'}{fmtTradeAmount(amount)}
                </span>
            )
        },
    },
    {
        title: '状态',
        dataIndex: 'status',
        width: 100,
        render: (v: string) => statusLabel[String(v || '').toLowerCase()] || v || '--',
    },
]

export default function TradeHistory({ portfolioId }: { portfolioId: number }) {
    const [items, setItems] = useState<RebalanceItem[]>([])
    const [rebalanceTotal, setRebalanceTotal] = useState(0)
    const [rebalancePage, setRebalancePage] = useState(1)
    const [orders, setOrders] = useState<TradingOrder[]>([])
    const [ordersTotal, setOrdersTotal] = useState(0)
    const [ordersPage, setOrdersPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [fillsLoading, setFillsLoading] = useState(true)

    const loadRebalances = useCallback((page: number) => {
        setLoading(true)
        const offset = (page - 1) * PAGE_SIZE
        getPortfolioRebalances(portfolioId, PAGE_SIZE, offset)
            .then(res => {
                setItems(res.items ?? [])
                setRebalanceTotal(res.total ?? 0)
                setRebalancePage(page)
            })
            .catch(() => {
                setItems([])
                setRebalanceTotal(0)
            })
            .finally(() => setLoading(false))
    }, [portfolioId])

    const loadOrders = useCallback((page: number) => {
        setFillsLoading(true)
        getTradingOrders(portfolioId, { page, size: PAGE_SIZE, filledOnly: true })
            .then(res => {
                setOrders(res.items ?? [])
                setOrdersTotal(res.total ?? 0)
                setOrdersPage(page)
            })
            .catch(() => {
                setOrders([])
                setOrdersTotal(0)
            })
            .finally(() => setFillsLoading(false))
    }, [portfolioId])

    useEffect(() => {
        setRebalancePage(1)
        setOrdersPage(1)
        loadRebalances(1)
        loadOrders(1)
    }, [portfolioId, loadRebalances, loadOrders])

    if (loading && fillsLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin /></div>
    }

    return (
        <div className="asset-detail__trade-list">
            <div className="asset-detail__trade-header">
                <span className="asset-detail__trade-title">全部交易记录</span>
                <span className="asset-detail__trade-count">{rebalanceTotal} 笔</span>
            </div>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><Spin /></div>
            ) : items.length === 0 ? (
                <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-3)' }}>暂无 AI 调仓下单记录</div>
            ) : (
                <>
                    {items.map(t => {
                        const tone = resolveTone(t)
                        const cfg = toneConfig[tone] ?? { label: t.action || '调仓', color: '#64748b' }
                        const shares = Number(t.shares) || 0
                        const price = Number(t.price) || 0
                        const name = (t.code_name || '').trim() || t.code || '--'
                        const isLimit = String(t.order_type || 'market').toLowerCase() === 'limit'
                        const orderType = isLimit ? '限价' : '市价'

                        return (
                            <div key={t.id} className="asset-detail__trade-row">
                                <div className="asset-detail__trade-left">
                                    <div className="asset-detail__trade-date">{t.date}</div>
                                    <span
                                        className="asset-detail__trade-type"
                                        style={{ background: `${cfg.color}20`, color: cfg.color }}
                                    >
                                        {cfg.label}
                                    </span>
                                </div>
                                <div className="asset-detail__trade-main">
                                    <div className="asset-detail__trade-stock">
                                        {name}
                                        {t.code && name !== t.code && (
                                            <span className="asset-detail__trade-code">{t.code}</span>
                                        )}
                                    </div>
                                    {t.reason && <div className="asset-detail__trade-reason">{t.reason}</div>}
                                </div>
                                <div className="asset-detail__trade-right">
                                    <div className="asset-detail__trade-detail">{orderType}</div>
                                    <div className="asset-detail__trade-detail">{fmtShares(shares)} 股</div>
                                    {isLimit && (
                                        <div className="asset-detail__trade-detail">{fmtPrice(price)}</div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                    {rebalanceTotal > PAGE_SIZE && (
                        <div className="asset-detail__trade-pagination">
                            <Pagination
                                size="small"
                                current={rebalancePage}
                                pageSize={PAGE_SIZE}
                                total={rebalanceTotal}
                                showSizeChanger={false}
                                showTotal={t => `共 ${t} 条`}
                                onChange={page => loadRebalances(page)}
                            />
                        </div>
                    )}
                </>
            )}

            <div className="asset-detail__trade-header" style={{ marginTop: 28 }}>
                <span className="asset-detail__trade-title">最终成交记录</span>
                <span className="asset-detail__trade-count">{ordersTotal} 笔</span>
            </div>
            {fillsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><Spin /></div>
            ) : (
                <Table
                    dataSource={orders}
                    columns={fillColumns}
                    rowKey="id"
                    size="small"
                    pagination={
                        ordersTotal > PAGE_SIZE
                            ? {
                                current: ordersPage,
                                pageSize: PAGE_SIZE,
                                total: ordersTotal,
                                showSizeChanger: false,
                                showTotal: t => `共 ${t} 条`,
                                onChange: page => loadOrders(page),
                            }
                            : false
                    }
                    locale={{ emptyText: '暂无最终成交记录' }}
                />
            )}
        </div>
    )
}
